// Real-time host-prefix validation hook (shared by register / edit / agent-confirm).
//
// Debounces the input, calls the authoritative backend endpoint
// (POST /api-services/host/validate via XApi.apiSvc.validateHost), and returns a
// localized status so the UI can tell the user *before submit* whether the host
// is allowed. Validation is advisory — the backend re-checks on create/update —
// but it removes the "type everything, submit, get rejected" round-trip.
//
// Race-safety: a monotonically increasing request id guards against out-of-order
// responses; only the latest in-flight request may update state.

import { useEffect, useRef, useState } from 'react';
import { XApi } from './services.js';
import { useT } from './i18n.jsx';

const DEBOUNCE_MS = 400;

// Map backend result → { tone, i18nKey } so the caller renders localized text.
// tone drives the inline hint colour: ok=green, checking=muted, invalid=red.
function describe(result) {
  if (result.ok) return { tone: 'ok', key: 'host.ok' };
  const code = result.code;
  const known = ['TOO_SHORT', 'INVALID_FORMAT', 'RESERVED', 'CONTAINS_BLOCKED', 'TAKEN'];
  return { tone: 'invalid', key: known.includes(code) ? `host.err.${code}` : 'host.err.default' };
}

/**
 * @param {string} host     raw host-prefix input (unnormalized ok — backend normalizes)
 * @param {object} [opts]
 * @param {string} [opts.excludeId]  edit mode: exclude self from uniqueness check
 * @param {string} [opts.skipValue]  edit mode grandfather: the service's *original* host.
 *   While the input still equals it (unchanged), stay idle — mirrors the backend, which
 *   only validates on host *change*, so a grandfathered reserved/taken host never alarms.
 * @param {boolean} [opts.enabled=true]  false ⇒ always idle (e.g. DIRECT mode, no host)
 * @returns {{ status:'idle'|'checking'|'ok'|'invalid', tone:string, message:string,
 *            normalized:string, available:(boolean|null), code:string|null }}
 */
export function useHostValidation(host, opts = {}) {
  const { excludeId, skipValue, enabled = true } = opts;
  const { t } = useT();
  const [state, setState] = useState({
    status: 'idle', tone: 'idle', code: null, normalized: '', available: null,
  });
  const reqIdRef = useRef(0);

  const trimmed = (host || '').trim();
  const original = (skipValue || '').trim().toLowerCase();
  const unchanged = original.length > 0 && trimmed.toLowerCase() === original;

  useEffect(() => {
    // Disabled, empty, or grandfathered (unchanged original host) → idle.
    if (!enabled || trimmed.length === 0 || unchanged) {
      reqIdRef.current += 1; // invalidate in-flight
      setState({ status: 'idle', tone: 'idle', code: null, normalized: '', available: null });
      return;
    }

    const myId = ++reqIdRef.current;
    let cancelled = false;

    // Show "checking" immediately (debounced request follows) so the field feels live.
    setState((s) => ({ ...s, status: 'checking', tone: 'checking' }));

    const timer = setTimeout(async () => {
      try {
        const result = await XApi.apiSvc.validateHost(trimmed, excludeId);
        if (cancelled || reqIdRef.current !== myId) return; // stale
        const d = describe(result);
        setState({
          status: result.ok ? 'ok' : 'invalid',
          tone: d.tone,
          code: result.code ?? null,
          normalized: result.normalized ?? '',
          available: result.available ?? null,
          _key: d.key,
        });
      } catch (e) {
        if (cancelled || reqIdRef.current !== myId) return;
        // Network / throttle error: don't block the user on advisory validation —
        // fall back to idle so submit still works (backend remains authoritative).
        if (e && (e.name === 'AbortError' || e.code === 20)) return;
        // Surface the reason (don't swallow silently) — a broken client method or a
        // 5xx should be visible in the console, otherwise the field just looks "fake".
        // eslint-disable-next-line no-console
        console.warn('[host-validate] check failed, degrading to idle:', e);
        setState({ status: 'idle', tone: 'idle', code: null, normalized: '', available: null });
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmed, excludeId, enabled, unchanged]);

  // Derive the localized message at render time (locale-reactive).
  let message = '';
  if (state.status === 'checking') message = t('host.checking');
  else if (state.status === 'ok') message = t('host.ok', { host: state.normalized || trimmed });
  else if (state.status === 'invalid') {
    message = t(state._key || 'host.err.default', { host: state.normalized || trimmed });
  }

  return {
    status: state.status,
    tone: state.tone,
    message,
    normalized: state.normalized,
    available: state.available,
    code: state.code,
  };
}
